import { useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";

import type {
  FramingAvailability,
  FramingSceneInspector,
  FramingStageAvailabilityKey,
  FramingStageKey,
  FramingStageView,
} from "../framingInspector";
import { displayDimensionsForSpace } from "../framingOverlayMath";
import { FramingEvidenceState } from "./FramingEvidenceState";
import { FramingViewportAdapter } from "./FramingViewportAdapter";
import {
  FRAMING_OVERLAY_CLASS_PATTERNS,
  buildOverlayShapes,
  type FramingOverlayClass,
  type FramingOverlayPresetKey,
  type FramingOverlayShape,
  type FramingOverlayWarning,
} from "./framingOverlayPresets";
import type { FramingViewportCamera } from "./framingViewportMath";

export const STAGE_TO_AVAILABILITY_KEY: Record<
  FramingStageKey,
  FramingStageAvailabilityKey
> = {
  raw: "source_native",
  working: "source_working",
  provider: "provider_input",
  generated: "provider_output",
  pre_effect: "post_effect",
  encoded: "encoded_delivery",
};

const OVERLAY_CLASS_COLORS: Record<FramingOverlayClass, string> = {
  target: "#2563eb",
  observation: "#0891b2",
  safety_region: "#16a34a",
  delivered_subject: "#9333ea",
  planned_crop: "#f59e0b",
  applied_crop: "#dc2626",
  provider_safe_zone: "#64748b",
  effect_viewport: "#db2777",
  tracking_path: "#0d9488",
  visible_content: "#a3a3a3",
  unrecognized: "#737373",
};

export type FramingStagePaneModel = {
  stage: FramingStageView;
  availability: FramingAvailability;
  shapes: FramingOverlayShape[];
  warnings: FramingOverlayWarning[];
};

export function buildStagePaneModel(
  inspector: FramingSceneInspector,
  stage: FramingStageView,
  preset: FramingOverlayPresetKey,
  timeSeconds: number,
  enabledClasses?: readonly FramingOverlayClass[],
): FramingStagePaneModel {
  const availabilityKey = STAGE_TO_AVAILABILITY_KEY[stage.key];
  const serverAvailability = inspector.stageAvailability[availabilityKey];
  const hasSignedMedia = Boolean(stage.artifact?.url);
  const availability: FramingAvailability = hasSignedMedia
    ? "available"
    : (serverAvailability ??
      (stage.coordinateSpace
        ? stage.coordinateSpace.retentionState === "ephemeral"
          ? "ephemeral"
          : stage.coordinateSpace.retentionState === "external"
            ? "external"
            : "projection_only"
        : "not_recorded"));
  const { shapes, warnings } = stage.coordinateSpace
    ? buildOverlayShapes(
        inspector,
        stage.coordinateSpace.id,
        preset,
        timeSeconds,
        enabledClasses,
      )
    : { shapes: [], warnings: [] };
  return { stage, availability, shapes, warnings };
}

/**
 * Aspect-adaptive stage pane (CROP-WB-007). The wrapper takes the true
 * media aspect ratio, so normalized geometry projects exactly onto pixels
 * with no fixed 16:9 assumption; letterbox padding never receives overlays.
 */
export function FramingStagePane({
  model,
  camera,
  onCameraChange,
  timeSeconds,
  onMediaError,
  highlightedShapeIndex,
  linkedDisabledReason,
}: {
  model: FramingStagePaneModel;
  camera: FramingViewportCamera | null;
  onCameraChange: (camera: FramingViewportCamera) => void;
  timeSeconds: number;
  onMediaError?: () => void;
  highlightedShapeIndex?: number | null;
  linkedDisabledReason?: string | null;
}) {
  const { stage, availability, shapes, warnings } = model;
  const [decodeFailed, setDecodeFailed] = useState(false);
  const space = stage.coordinateSpace;
  const display = useMemo(
    () =>
      displayDimensionsForSpace(space ?? null, space ? [space] : []) ??
      (space?.width && space.height
        ? { width: space.width, height: space.height, orientation: "normal" }
        : null),
    [space],
  );
  const artifact = stage.artifact;
  const isVideo =
    (artifact?.contentType ?? "").startsWith("video/") ||
    (artifact?.key ?? "").endsWith(".mp4") ||
    (artifact?.key ?? "").endsWith(".webm");
  const aspect =
    display && display.width > 0 && display.height > 0
      ? `${display.width} / ${display.height}`
      : "16 / 9";
  const rootId = space?.id ?? `stage-${stage.key}`;
  const showMedia =
    availability === "available" && artifact?.url && !decodeFailed;

  return (
    <figure
      className="flex min-w-0 flex-col gap-1 rounded-md border p-2"
      data-testid={`framing-stage-pane-${stage.key}`}
    >
      <figcaption className="flex flex-wrap items-center justify-between gap-1 text-xs">
        <span className="font-medium">{stage.label}</span>
        <span className="flex flex-wrap items-center gap-1 text-muted-foreground">
          {display ? (
            <span className="font-mono">
              {display.width}x{display.height}
              {display.orientation !== "normal"
                ? ` (${display.orientation})`
                : ""}
            </span>
          ) : (
            <span>dimensions unknown</span>
          )}
          {space ? <Badge variant="outline">root {space.id}</Badge> : null}
          {space?.transformFromParent?.kind ? (
            <Badge variant="outline">{space.transformFromParent.kind}</Badge>
          ) : null}
          {stage.key === "generated" ? (
            <Badge variant="secondary">new coordinate root</Badge>
          ) : null}
        </span>
      </figcaption>
      <div
        className="relative w-full bg-muted/40"
        style={{ aspectRatio: aspect }}
      >
        {showMedia ? (
          <FramingViewportAdapter
            rootId={rootId}
            camera={camera}
            onCameraChange={onCameraChange}
            label={`${stage.label} viewport`}
          >
            <div className="relative h-full w-full">
              {isVideo ? (
                <video
                  key={artifact!.url!}
                  src={artifact!.url!}
                  className="h-full w-full object-contain"
                  preload="metadata"
                  muted
                  playsInline
                  onError={() => {
                    setDecodeFailed(true);
                    onMediaError?.();
                  }}
                  data-scene-time={timeSeconds}
                />
              ) : (
                <img
                  src={artifact!.url!}
                  alt={`${stage.label} evidence pixels`}
                  className="h-full w-full object-contain"
                  draggable={false}
                  onError={() => {
                    setDecodeFailed(true);
                    onMediaError?.();
                  }}
                />
              )}
              <svg
                aria-hidden={shapes.length === 0}
                role={shapes.length > 0 ? "img" : undefined}
                aria-label={
                  shapes.length > 0
                    ? `${shapes.length} geometry overlays on ${stage.label}`
                    : undefined
                }
                className="pointer-events-none absolute inset-0 h-full w-full"
                viewBox="0 0 1 1"
                preserveAspectRatio="none"
              >
                {shapes.map((shape) => (
                  <g key={`${shape.overlayClass}-${shape.index}`}>
                    <rect
                      x={shape.box.xMin}
                      y={shape.box.yMin}
                      width={shape.box.xMax - shape.box.xMin}
                      height={shape.box.yMax - shape.box.yMin}
                      fill="none"
                      stroke={OVERLAY_CLASS_COLORS[shape.overlayClass]}
                      strokeWidth={
                        highlightedShapeIndex === shape.index ? 0.008 : 0.004
                      }
                      strokeDasharray={
                        FRAMING_OVERLAY_CLASS_PATTERNS[shape.overlayClass] ===
                        "0"
                          ? undefined
                          : FRAMING_OVERLAY_CLASS_PATTERNS[shape.overlayClass]
                              .split(" ")
                              .map((value) => Number(value) / 200)
                              .join(" ")
                      }
                      vectorEffect="non-scaling-stroke"
                    >
                      <title>{`${shape.index}. ${shape.label}${
                        shape.provenance !== "exact"
                          ? ` (${shape.provenance})`
                          : ""
                      }`}</title>
                    </rect>
                    <text
                      x={Math.min(shape.box.xMin + 0.008, 0.94)}
                      y={Math.max(shape.box.yMin - 0.012, 0.03)}
                      fontSize={0.028}
                      fill={OVERLAY_CLASS_COLORS[shape.overlayClass]}
                      stroke="white"
                      strokeWidth={0.004}
                      paintOrder="stroke"
                    >
                      {shape.index}
                    </text>
                  </g>
                ))}
              </svg>
            </div>
          </FramingViewportAdapter>
        ) : (
          <FramingEvidenceState
            availability={decodeFailed ? "decode_unsupported" : availability}
            onRetry={onMediaError ?? null}
            compact
          />
        )}
      </div>
      {linkedDisabledReason ? (
        <p className="text-[11px] text-muted-foreground">
          {linkedDisabledReason}
        </p>
      ) : null}
      {warnings.length > 0 ? (
        <p className="text-[11px] text-amber-600" role="note">
          {warnings.map((warning) => warning.reason).join(" ")}
        </p>
      ) : null}
    </figure>
  );
}
